import { useCallback, useEffect, useRef, useState } from 'react';
import useAuth from './useAuth';
import { getNextSortOrder } from './NextSortOrder';

export interface ITableParams<TFilters, TSortField extends string = string> {
    pagination: {
        current: number;
        pageSize: number;
    };
    filters: TFilters;
    sorter: {
        field: TSortField;
        order: 'asc' | 'desc';
    };
}

export interface IUseTableParamsProps<TFilters, TSortField extends string = string> {
    storageKey?: string;
    defaultFilters: TFilters;
    defaultSort: {
        field: TSortField;
        order: 'asc' | 'desc';
    };
    defaultPageSize?: number;
    resetOnCityChange?: boolean;
}

export function useTableParams<TFilters extends Record<string, any>, TSortField extends string = string>({
    storageKey,
    defaultFilters,
    defaultSort,
    defaultPageSize = 100,
    resetOnCityChange = true,
}: IUseTableParamsProps<TFilters, TSortField>) {
    const { activeCity } = useAuth();
    const lastSorterRef = useRef<{ field: string; order: string } | any>(defaultSort);

    const defaultParams: ITableParams<TFilters, TSortField> = {
        pagination: {
            current: 1,
            pageSize: defaultPageSize,
        },
        filters: defaultFilters,
        sorter: defaultSort,
    };

    const [tableParams, setTableParams] = useState<ITableParams<TFilters, TSortField>>(() => {
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                try {
                    return JSON.parse(stored);
                } catch {
                    return defaultParams;
                }
            }
        }
        return defaultParams;
    });

    const updateParams = useCallback((updater: (prev: ITableParams<TFilters, TSortField>) => ITableParams<TFilters, TSortField>) => {
        setTableParams((prev) => {
            const next = updater(prev);
            if (storageKey) {
                localStorage.setItem(storageKey, JSON.stringify(next));
            }
            return next;
        });
    }, [storageKey]);

    const handleTableChange = useCallback((
        pagination: any,
        filters: any,
        sorter: any,
        _extra?: any
    ) => {
        const sorterObj = Array.isArray(sorter) ? sorter[0] : sorter;

        const { nextSort, nextOrder } = getNextSortOrder(
            sorterObj,
            tableParams,
            tableParams.pagination.current,
            lastSorterRef
        );

        updateParams((prev) => {
            const mergedFilters = { ...prev.filters };

            if (filters) {
                Object.keys(filters).forEach((key) => {
                    const val = filters[key];
                    if (val !== undefined) {
                        (mergedFilters as any)[key] = val && val.length > 0 ? val[0] : null;
                    }
                });
            }

            return {
                pagination: {
                    current: pagination?.current || 1,
                    pageSize: pagination?.pageSize || prev.pagination.pageSize,
                },
                filters: mergedFilters,
                sorter: {
                    field: (nextSort || prev.sorter.field) as TSortField,
                    order: (nextOrder || prev.sorter.order) as 'asc' | 'desc',
                },
            };
        });
    }, [tableParams, updateParams]);

    const handleCustomChange = useCallback(({
        newPage,
        newSize,
        newSort,
        newOrder,
        newFilters,
    }: {
        newPage?: number;
        newSize?: number;
        newSort?: TSortField;
        newOrder?: 'asc' | 'desc';
        newFilters?: Partial<TFilters>;
    }) => {
        updateParams((prev) => ({
            pagination: {
                current: newPage ?? prev.pagination.current,
                pageSize: newSize ?? prev.pagination.pageSize,
            },
            filters: newFilters ? { ...prev.filters, ...newFilters } : prev.filters,
            sorter: {
                field: newSort ?? prev.sorter.field,
                order: newOrder ?? prev.sorter.order,
            },
        }));
    }, [updateParams]);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        updateParams((prev) => ({
            ...prev,
            pagination: {
                current: page,
                pageSize,
            },
        }));
    }, [updateParams]);

    const handleFilterClear = useCallback((field: keyof TFilters) => {
        updateParams((prev) => ({
            ...prev,
            pagination: { ...prev.pagination, current: 1 },
            filters: {
                ...prev.filters,
                [field]: null,
            },
        }));
    }, [updateParams]);

    const handleClearAllFilters = useCallback(() => {
        updateParams((prev) => ({
            ...prev,
            pagination: { ...prev.pagination, current: 1 },
            filters: defaultFilters,
        }));
    }, [defaultFilters, updateParams]);

    const hasActiveFilters = Object.values(tableParams.filters || {}).some(
        (val) => val !== null && val !== undefined && val !== ''
    );

    useEffect(() => {
        if (resetOnCityChange && activeCity?.id) {
            updateParams((prev) => ({
                ...prev,
                pagination: { ...prev.pagination, current: 1 },
            }));
        }
    }, [activeCity?.id, resetOnCityChange, updateParams]);

    return {
        tableParams,
        setTableParams,
        updateParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
        hasActiveFilters,
        lastSorterRef,
    };
}
